import { Copy, Folder, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { deleteFile, fileServeUrl, listFiles, uploadFile } from "@/features/admin/api";
import type { FileAsset, FileListing } from "@/features/admin/types";
import { ApiError } from "@/lib/api";

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FilesPage() {
  const [path, setPath] = useState("");
  const [listing, setListing] = useState<FileListing | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<FileAsset | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function refresh(p: string) {
    try {
      setListing(await listFiles(p));
      setPath(p);
    } catch {
      toast.error("Couldn't list files.");
    }
  }

  useEffect(() => {
    void refresh("");
  }, []);

  async function handleUpload(file: File) {
    setUploading(true);
    try {
      await uploadFile(path, file);
      await refresh(path);
      toast.success("Uploaded.");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't upload.";
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteFile(deleting.path);
      setDeleting(null);
      await refresh(path);
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  }

  async function copyUrl(file: FileAsset) {
    try {
      await navigator.clipboard.writeText(fileServeUrl(file.path));
      toast.success("URL copied.");
    } catch {
      toast.error("Couldn't copy the URL.");
    }
  }

  const crumbs = path ? path.split("/") : [];

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <CardHeader className="flex flex-row items-start justify-between gap-4 p-4">
        <div>
          <CardTitle>Internal File Assets</CardTitle>
          <CardDescription>
            Vendor downloadables referenced from media / resource pages. Uploaded here, served to
            firm users by URL.
          </CardDescription>
          <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-1 text-sm">
            <button type="button" className="hover:text-primary" onClick={() => void refresh("")}>
              root
            </button>
            {crumbs.map((c, i) => {
              const to = crumbs.slice(0, i + 1).join("/");
              return (
                <span key={to} className="flex items-center gap-1">
                  <span>/</span>
                  <button type="button" className="hover:text-primary" onClick={() => void refresh(to)}>
                    {c}
                  </button>
                </span>
              );
            })}
          </div>
        </div>
        <div>
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
            }}
          />
          <Button size="sm" className="gap-1.5" disabled={uploading} onClick={() => fileInput.current?.click()}>
            <Upload className="size-4" /> {uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {!listing ? (
          <p className="text-muted-foreground p-4 text-sm">Loading…</p>
        ) : listing.dirs.length === 0 && listing.files.length === 0 ? (
          <p className="text-muted-foreground p-4 text-sm">This folder is empty.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listing.dirs.map((dir) => (
                  <TableRow key={dir.path}>
                    <TableCell>
                      <button
                        type="button"
                        className="flex items-center gap-2 font-medium hover:underline"
                        onClick={() => void refresh(dir.path)}
                      >
                        <Folder className="size-4" /> {dir.name}
                      </button>
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell />
                  </TableRow>
                ))}
                {listing.files.map((file) => (
                  <TableRow key={file.path}>
                    <TableCell className="font-medium">
                      <a
                        href={fileServeUrl(file.path)}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        {file.name}
                      </a>
                    </TableCell>
                    <TableCell>{humanSize(file.size)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" aria-label="Copy URL" onClick={() => void copyUrl(file)}>
                          <Copy className="size-4" />
                        </Button>
                        <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => setDeleting(file)}>
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDelete()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
